/**
 * /platform — Platform Admin landing hub.
 *
 * Renders 4 prominent cards as one-click access to the platform admin
 * tools. Replaces /admin as the default post-login landing for
 * platform_admin users (see App.js redirect logic).
 */
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Building2, Users, TrendingUp, Trash2, ShieldAlert, ArrowRight } from 'lucide-react';
import { api } from '../../lib/api';

const TOOLS = [
  {
    to: '/platform/clinics',
    title: 'Clinics',
    description: 'Tenant directory: search, inspect plan/status, suspend, or reactivate any clinic.',
    icon: Building2,
    accent: 'from-[#003D80] to-[#1A5AAB]',
  },
  {
    to: '/platform/subscribers',
    title: 'Subscribers',
    description: 'Stripe billing snapshot: MRR, plan distribution, usage caps, dunning state.',
    icon: Users,
    accent: 'from-[#3A664D] to-[#2E5640]',
  },
  {
    to: '/platform/revenue',
    title: 'Revenue',
    description: 'Daily/weekly/monthly MRR + ARPU + churn cohort + LTV by acquisition channel.',
    icon: TrendingUp,
    accent: 'from-[#A86F3D] to-[#8B5A2B]',
  },
  {
    to: '/platform/users',
    title: 'Purge user',
    description: 'Cascade-delete any user across all collections. Audit-logged. Destructive.',
    icon: Trash2,
    accent: 'from-[#B83A3A] to-[#8E2828]',
    destructive: true,
  },
];

export default function PlatformHome() {
  const [stats, setStats] = useState(null);
  useEffect(() => {
    // Best-effort — pull a tiny snapshot to render on the hero strip.
    // Endpoint is already used by /platform/clinics so no new backend work.
    api.get('/platform/clinics?limit=1').then(r => {
      setStats({ total_clinics: r.data?.total ?? null });
    }).catch(() => setStats({ total_clinics: null }));
  }, []);

  return (
    <div className="p-4 md:p-8 max-w-6xl">
      <div className="mb-8">
        <div className="text-xs uppercase tracking-[0.2em] text-[#B83A3A] font-medium mb-2 inline-flex items-center gap-1">
          <ShieldAlert className="w-3 h-3"/> PLATFORM CONTROL
        </div>
        <h1 className="text-4xl md:text-5xl font-heading font-bold tracking-tight">Platform admin home</h1>
        <p className="text-fg-muted text-base mt-2 max-w-2xl">
          One-click access to every SaaS-operator tool. Every action here is
          audit-logged and applies across all tenants — handle with care.
        </p>
        {stats && stats.total_clinics !== null && (
          <div className="mt-4 inline-flex items-center gap-2 text-sm text-fg-muted" data-testid="platform-stats-clinics">
            <Building2 className="w-4 h-4"/>
            <span><strong className="text-fg">{stats.total_clinics}</strong> clinics currently provisioned</span>
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {TOOLS.map((t) => {
          const Icon = t.icon;
          return (
            <Link
              key={t.to}
              to={t.to}
              data-testid={`platform-card-${t.to.split('/').pop()}`}
              className={`group block rounded-xl p-5 border ${t.destructive ? 'border-red-300' : 'border-soft'} surface hover:shadow-lg transition-all hover:-translate-y-0.5`}
            >
              <div className={`w-12 h-12 rounded-lg bg-gradient-to-br ${t.accent} flex items-center justify-center mb-3`}>
                <Icon className="w-6 h-6 text-white" strokeWidth={1.8}/>
              </div>
              <div className="flex items-center justify-between gap-2 mb-1">
                <h2 className="font-heading font-bold text-lg">{t.title}</h2>
                <ArrowRight className="w-4 h-4 text-fg-muted group-hover:text-brand group-hover:translate-x-1 transition-all"/>
              </div>
              <p className="text-sm text-fg-secondary leading-relaxed">{t.description}</p>
              {t.destructive && (
                <div className="mt-3 text-[10px] uppercase tracking-wider text-[#B83A3A] font-semibold">
                  Destructive · Audit-logged
                </div>
              )}
            </Link>
          );
        })}
      </div>
    </div>
  );
}
